<template>
  <div class="pm-dialog-port-tree flex flex-col gap-5">
    <div class="pm-dialog-port-tree-select-boxes flex flex-col gap-4">
      <div class="flex items-center gap-6">
        <div class="header font-bold">島・港</div>
        <v-select
          class="display-items-select-box flex-grow"
          v-model="selectedPort"
          :options="portOptions"
          label="label"
          placeholder="選択してください"
          :clearable="false"
          :searchable="false"
        >
          <template #selected-option="{}">
            <div style="display: flex; align-items: baseline">
              {{ selectedPort && selectedPort.label }}
            </div>
          </template>
          <template v-slot:no-options>
            <div>選択可能な港がありません</div>
          </template>
        </v-select>
      </div>
      <div class="flex items-center gap-6">
        <div class="header font-bold">岸壁</div>
        <v-select
          class="display-items-select-box flex-grow"
          v-model="selectedQuay"
          :options="quayOptions"
          label="label"
          placeholder="選択してください"
          :clearable="false"
          :searchable="false"
        >
          <template #selected-option="{}">
            <div style="display: flex; align-items: baseline">
              {{ selectedQuay && selectedQuay.label }}
            </div>
          </template>
          <template v-slot:no-options>
            <div>選択可能な岸壁がありません</div>
          </template>
        </v-select>
      </div>
      <div class="flex items-center gap-6">
        <div class="header font-bold">バース</div>
        <v-select
          class="display-items-select-box flex-grow"
          v-model="selectedBerth"
          :options="berthOptions"
          label="label"
          placeholder="選択してください"
          :clearable="false"
          :searchable="false"
        >
          <template #selected-option="{}">
            <div style="display: flex; align-items: baseline">
              {{ selectedBerth && selectedBerth.label }}
            </div>
          </template>
          <template v-slot:no-options>
            <div>選択可能なバースがありません</div>
          </template>
        </v-select>
      </div>
    </div>
    <div class="flex justify-center">
      <vs-button class="button" color="#2E87B9" @mousedown="closePopup">
        <span class="button-text">キャンセル</span>
      </vs-button>
      <vs-button
        class="button"
        color="#2E87B9"
        @mousedown="onSubmit(isUpdateTimeStamp)"
        :disabled="!isValid"
      >
        <span class="button-text">保存</span>
      </vs-button>
    </div>
  </div>
</template>

<script>
/*
 * 入出港予定一覧画面（白色ではなく黒色画面）で、島、港、岸壁、バースを編集する
 * @parent CellSummaryDialog.vue
 * @props columnKey: String カラム名を特定するためのキー。const.jsのCOL_NAMEの中の、columnKey
 *        valueMap : String 現在表示している値。
 *        taskId: String 一覧画面で表示する一行のデータにふられたID
 *        closePopup: ポップアップを閉じる関数
 *        selectedTask: 選択したカラムが属するレコードの情報
 */

import { createNamespacedHelpers } from "vuex";
const { mapState } = createNamespacedHelpers("gkmgPortMasterDestinationWork");
import moment from "moment-timezone";
import vSelect from "vue-select";
import isArray from "lodash/isArray";

export default {
  name: "O.DialogPortTrees",
  components: {
    vSelect,
  },
  props: {
    taskId: { type: String, required: true },
    columnKey: { type: String, required: true },
    valueMap: { type: Object, required: true },
    closePopup: { type: Function, required: true },
    selectedTask: { type: Object, required: true },
  },
  data() {
    return {
      // selectedIsland: null, // portから導く
      selectedPort: null,
      selectedQuay: null, // port変更で、初期化
      selectedBerth: null, // port変更・quay変更で、初期化
      isUpdateTimeStamp: false,
    };
  },
  computed: {
    ...mapState({
      summaryDialogShowPopup: (state) => state.summaryDialogShowPopup,
      masterMap: (state) => state.masterMap,
    }),
    hasIslandMaster() {
      return this.masterMap && isArray(this.masterMap.islands);
    },
    islands() {
      if (!this.hasIslandMaster) return [];
      return this.masterMap.islands;
    },
    hasQuayMaster() {
      return this.masterMap && isArray(this.masterMap.quays);
    },
    quays() {
      if (!this.hasQuayMaster) return [];
      return this.masterMap.quays;
    },
    portOptions() {
      if (!(this.masterMap && Array.isArray(this.masterMap.ports))) return [];
      return this.masterMap.ports.map((port) => ({
        ...port,
        label: `${this.islandMapByPortId[port.id].label} ${port.label}`,
      }));
    },
    quayOptions() {
      if (!this.selectedPort) return [];
      return this.quayMapByPortId[this.selectedPort.id] || [];
    },
    berthOptions() {
      if (!this.selectedQuay) return [];
      return Array.from(
        { length: this.selectedQuay.max_berth || 0 },
        (_v, i) => ({ label: i + 1, id: i + 1 }),
      );
    },
    initialPortId() {
      if (!this.valueMap) return null;
      return this.valueMap.portId;
    },
    initialQuayId() {
      if (!this.valueMap) return null;
      return this.valueMap.quayId;
    },
    initialBerthId() {
      if (!this.valueMap) return null;
      return this.valueMap.berthId;
    },
    islandMapByPortId() {
      if (!(this.masterMap && this.masterMap.island_tree)) return {};
      return Object.keys(this.masterMap.island_tree).reduce(
        (acc, islandKey) => {
          const ports = this.masterMap.island_tree[islandKey];
          Object.keys(ports).forEach((portKey) => {
            acc[this.getIdFromTreeKey(portKey)] = this.islands.find(
              (island) => island.id === this.getIdFromTreeKey(islandKey),
            );
          });
          return acc;
        },
        {},
      );
    },
    quayMapByPortId() {
      if (!(this.masterMap && this.masterMap.island_tree && this.hasQuayMaster))
        return {};
      return Object.values(this.masterMap.island_tree).reduce((acc, ports) => {
        Object.keys(ports).forEach((portKey) => {
          acc[this.getIdFromTreeKey(portKey)] = ports[portKey].quay_ids.map(
            (quayId) => this.quays.find((quay) => quay.id === quayId),
          );
        });
        return acc;
      }, {});
    },
    selectedIsland() {
      if (!this.selectedPort) return null;
      return this.islandMapByPortId[this.selectedPort.id];
    },
    selectedIslandLabel() {
      const { selectedIsland } = this;
      if (!selectedIsland) return "---";
      return selectedIsland.label || "---";
    },
    isValid() {
      return !!(
        this.selectedIsland &&
        this.selectedPort &&
        this.selectedQuay &&
        this.selectedBerth
      );
    },
  },
  watch: {
    summaryDialogShowPopup() {
      if (this.summaryDialogShowPopup) {
        this.initData();
      } else {
        this.$nextTick(() => {
          setTimeout(() => {
            this.resetData();
          }, 200);
        });
      }
    },
    selectedPort(_val, oldVal) {
      if (oldVal) {
        this.selectedQuay = null;
      }
    },
    selectedQuay(_val, oldVal) {
      if (oldVal) {
        this.selectedBerth = null;
      }
    },
  },
  methods: {
    initData() {
      this.selectedPort = this.portOptions
        .filter((port) => port && port.id)
        .find((port) => String(port.id) === String(this.initialPortId));
      this.selectedQuay = this.quayOptions
        .filter((quay) => quay && quay.id)
        .find((quay) => String(quay.id) === String(this.initialQuayId));
      this.selectedBerth = this.berthOptions.find(
        (berth) => String(berth.id) === String(this.initialBerthId),
      );
      // 現状、changesはデバッグとしてのみ使用
      // ハイライト保存の件は使用していない
      this.isUpdateTimeStamp =
        this.valueMap.changes && this.valueMap.changes[String(this.columnKey)]
          ? true
          : false;
    },
    resetData() {
      this.selectedPort = null;
      this.selectedQuay = null;
      this.selectedBerth = null;
    },
    onSubmit(isUpdateTimeStamp) {
      /*
       * onSubmit(false)    : 保存
       * onSubmit(true)     : ハイライト保存
       */
      const mode = [];
      if (isUpdateTimeStamp) mode.push("updateTimeStamp");
      const updateKeyValueMap = {};

      updateKeyValueMap["port_id"] = Number(this.selectedPort.id);
      updateKeyValueMap["quay_id"] = Number(this.selectedQuay.id);
      updateKeyValueMap["berth_id"] = Number(this.selectedBerth.id);

      updateKeyValueMap["updated_at"] = new Date(
        moment().local().format("YYYY-MM-DD HH:mm:ss"),
      );

      this.$emit("updateValue", this.taskId, updateKeyValueMap, mode);
    },
    /**
     * island_treeのkeyからidを抜き出す
     * @param string treeKey
     * @returns {string|null} treeId
     */
    getIdFromTreeKey(treeKey) {
      const treeId = treeKey.replaceAll(/^.*:/g, "");
      if (treeId === "") return null;
      return treeId;
    },
  },
  mounted() {
    this.initData();
  },
  beforeDestroy() {
    this.resetData();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/port-master/_variables.scss";
.pm-dialog-port-tree {
  .pm-dialog-port-tree-select-boxes {
    .header {
      min-width: 4rem;
    }
  }

  ::v-deep .vs-input-number--button-less {
    display: none !important;
  }
  ::v-deep .vs-input-number--button-plus {
    display: none !important;
  }

  .button {
    width: 230px;
    height: 46px;
    margin: 0 1rem;
    border-radius: 8px;
    cursor: pointer;
  }

  .button-text {
    width: 32px;
    height: 24px;

    font-family: "Hiragino Kaku Gothic Pro";
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    /* identical to box height */
    align-items: center;
    text-align: center;

    color: #f2f2f2;
  }
  .text-input {
    width: 230px;
    height: 46px;
    margin: 0 1rem;
    border-radius: 8px;

    font-family: "Hiragino Kaku Gothic Pro";
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;

    align-items: center;
    display: flex;
    text-align: center;

    color: #4f4f4f;
    background-color: #f2f2f2;
    border: none;
  }
}
</style>
